/**
 * 064: the single source of every sentence the app uses to state that a read did not produce
 * content (FR-011).
 *
 * Two render paths need this wording, which is why it lives here rather than inside
 * `ResultState.tsx`:
 *
 *  - `ResultState` states a **primary** read's outcome, as a centred block that owns the screen.
 *  - A **supporting lookup** must state itself at the place it belongs to and leave the rest of
 *    the screen usable (FR-010, C2.6), so it renders inline beside its own control — a centred
 *    full-height block beside a dropdown would be wrong.
 *
 * Both paths take their words from here. A screen supplies only the domain noun (C2.8) — the
 * screen is the only thing that knows whether a car or a booking was being read — and never a
 * sentence of its own.
 */

/** A read refused because the session is no longer valid. */
export const SESSION_ENDED = 'Your session has ended.';

/** A read the platform refuses for this account. */
export const REFUSED = 'This is not available for your account.';

/** A read for something that no longer exists, where the screen supplied no noun. */
export const GONE = 'This is no longer available.';

/**
 * 069: a wait for a payment that ended without an answer.
 *
 * A third render path joins the two above: the post-payment screen polls for a confirmation and
 * has to say something when its budget is spent. That is not a `FetchState` arm — the read
 * succeeded, and answered `awaiting_payment` — so the screen renders it itself, but the sentence
 * belongs here with every other one.
 *
 * It does not claim the payment failed, because the app cannot observe that: a failed callback
 * leaves the booking `awaiting_payment` and the customer's payload publishes no payment outcome
 * at all (gap H24). "Not confirmed" is the strongest true statement available.
 */
export const PAYMENT_NOT_CONFIRMED_YET =
  'Your payment has not been confirmed yet. It may still come through — check this booking again in a few minutes.';

/**
 * 070: the platform accepted the request and never answered it (H25, FR-004).
 *
 * Deliberately **not** `NETWORK_FAILURE_REASON`. "Could not reach the service. Check your
 * connection." is false here — the service *was* reached, and it is the one that went quiet. Telling
 * a customer to check a connection that is working sends them to fix the wrong thing.
 *
 * Retryable, so it reaches the customer through the same `failed` arm as any other retryable
 * failure and `ResultState` offers **Try again** beside it without changing. A supporting lookup
 * keeps its own shorter in-place wording instead (FR-006): beside a dropdown, *which* lookup failed
 * is the useful fact, and one screen shows three of them at once.
 */
export const SERVICE_TOO_SLOW = 'The service took too long to respond. Please try again.';

/**
 * The subject of a primary read no longer exists.
 *
 * @param subject the domain noun, lowercase and unqualified — `'car'`, `'booking'`
 * @param plural whether the subject is a collection, so the sentence agrees
 */
export function goneMessage(subject: string, plural = false): string {
  return plural
    ? `These ${subject} are no longer available.`
    : `This ${subject} is no longer available.`;
}

/**
 * H19: a page number that is legal but holds nothing, of a collection that is **not** empty.
 *
 * Deliberately not `goneMessage`. Three outcomes share these screens and 066/H17 keep them apart:
 * the read failed (`goneMessage`), the page is out of range (here), the collection is genuinely
 * empty ("No bookings yet"). Saying "These bookings are no longer available." to a customer whose
 * history holds 45 of them is 064's defect one page over — the page is missing, the records are not.
 *
 * `page` is echoed by the platform and never clamped down to `pages` (071), so a stale bookmark or
 * a shrunken history lands here.
 *
 * @param subject the customer-owned collection, lowercase and plural — `'bookings'`, `'reviews'`
 */
export function pageGoneMessage(subject: string): string {
  return `That page of your ${subject} does not exist.`;
}

/**
 * A supporting lookup could not be read.
 *
 * @param subject what failed, capitalised as it appears at the start of the sentence
 * @param hint what the customer can still do, when the screen stays usable without the lookup
 */
export function lookupFailedMessage(subject: string, hint?: string): string {
  const stated = `${subject} could not be loaded.`;
  return hint ? `${stated} ${hint}` : stated;
}
